/**
 * AI 食物識別服務
 * 使用 Poe API (主) 與 OpenRouter API (備) 進行食物圖片識別和營養分析
 */

const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY || '';
const POE_API_KEY = import.meta.env.VITE_POE_API_KEY || '';
const OPENROUTER_BASE_URL = 'https://openrouter.ai/api/v1';
const POE_BASE_URL = 'https://api.poe.com/bot';

// 可用的視覺 AI 模型列表（包含來源標記）
const VISION_MODELS = [
  { id: 'gemini-3.1-flash-lite', provider: 'poe' }, // Poe 上的 Gemini 3.1 Flash Lite (預設首選)
  { id: 'gemini-3-flash', provider: 'poe' }, // Poe 上的 Gemini 3 Flash
  { id: 'GPT-4o-Mini', provider: 'poe' }, // Poe 上的 GPT-4o-Mini
  { id: 'google/gemini-3.1-flash-lite-preview', provider: 'openrouter' },
  { id: 'google/gemini-3.1-pro-preview', provider: 'openrouter' },
  { id: 'qwen/qwen3.5-9b', provider: 'openrouter' },
  { id: 'bytedance-seed/seed-1.6-flash', provider: 'openrouter' },
  { id: 'openai/gpt-5-nano', provider: 'openrouter' },
  { id: 'qwen/qwen3-vl-32b-instruct', provider: 'openrouter' }
];

/**
 * AI 服務類
 */
class AIService {
  constructor() {
    this.openRouterApiKey = OPENROUTER_API_KEY;
    this.poeApiKey = POE_API_KEY;
    this.openRouterBaseUrl = OPENROUTER_BASE_URL;
    this.poeBaseUrl = POE_BASE_URL;
    this.currentModelIndex = 0;

    // 調試：顯示載入的 API Key 狀態
    console.log('🤖 AI Service 初始化:');
    console.log(`   OpenRouter Key: ${this.openRouterApiKey ? '✅ 已設定 (' + this.openRouterApiKey.substring(0, 10) + '...)' : '❌ 未設定'}`);
    console.log(`   Poe Key: ${this.poeApiKey ? '✅ 已設定 (' + this.poeApiKey.substring(0, 10) + '...)' : '❌ 未設定'}`);
  }

  /**
   * 獲取可用的模型列表
   */
  getAvailableModels() {
    return VISION_MODELS.map(m => m.id);
  }

  /**
   * 獲取可用的模型列表（包含詳細資訊）
   */
  getAvailableModelsWithDetails() {
    return VISION_MODELS.map((model, index) => ({
      index,
      name: model.id,
      provider: model.provider,
      displayName: this.getModelDisplayName(model.id, model.provider),
      isCurrent: index === this.currentModelIndex
    }));
  }

  /**
   * 獲取模型的顯示名稱
   */
  getModelDisplayName(modelId, provider) {
    if (provider === 'poe') {
      return `[Poe] ${modelId}`;
    }
    const names = {
      'google/gemini-3.1-flash-lite-preview': 'Gemini 3.1 Flash Lite',
      'google/gemini-3.1-pro-preview': 'Gemini 3.1 Pro',
      'qwen/qwen3.5-9b': 'Qwen 3.5 (9B)',
      'bytedance-seed/seed-1.6-flash': 'ByteDance Seed Flash',
      'openai/gpt-5-nano': 'GPT-5 Nano',
      'qwen/qwen3-vl-32b-instruct': 'Qwen 3 VL (32B)',
      'google/gemini-2.5-flash-lite-preview-09-2025': 'Gemini 2.5 Flash Lite'
    };
    return `[OR] ${names[modelId] || modelId.split('/')[1] || modelId}`;
  }

  /**
   * 手動設定使用的模型
   */
  setModel(modelIndex) {
    if (modelIndex >= 0 && modelIndex < VISION_MODELS.length) {
      this.currentModelIndex = modelIndex;
      console.log(`🔄 切換到模型: ${VISION_MODELS[modelIndex].id} (${VISION_MODELS[modelIndex].provider})`);
      return true;
    }
    return false;
  }

  /**
   * 獲取當前使用的模型
   */
  getCurrentModel() {
    const model = VISION_MODELS[this.currentModelIndex];
    return {
      index: this.currentModelIndex,
      name: model.id,
      provider: model.provider,
      displayName: this.getModelDisplayName(model.id, model.provider)
    };
  }

  /**
   * 將圖片轉換為 base64 格式
   */
  async imageToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // 直接返回完整的 data URI (包含 mime type)
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * 識別食物圖片並分析營養成分
   */
  async identifyFood(imageFile, additionalInfo = '') {
    let lastError = null;
    
    // 嘗試所有可用的模型
    for (let i = 0; i < VISION_MODELS.length; i++) {
      const modelIndex = (this.currentModelIndex + i) % VISION_MODELS.length;
      const currentModelObj = VISION_MODELS[modelIndex];
      
      try {
        console.log(`🤖 嘗試使用模型: ${currentModelObj.id} (${currentModelObj.provider})`);
        
        // 將圖片轉換為 base64
        const base64Image = await this.imageToBase64(imageFile);
        
        // 構建提示詞
        const prompt = this.buildFoodIdentificationPrompt(additionalInfo);
        
        let response;
        let content;

        if (currentModelObj.provider === 'poe') {
           // 調用 Poe API
           if (!this.poeApiKey) {
              throw new Error('Poe API Key is not configured');
           }
           
           const keyPrefix = this.poeApiKey.substring(0, 15) + '...';
           console.log(`🔑 Using Poe API Key starting with: ${keyPrefix}`);

           // Note: Poe's official API currently requires a backend server to act as a bot to handle server-sent events (SSE).
           // However, if you are using an OpenAI-compatible proxy for Poe, it might look like this:
           response = await fetch(`https://api.poe.com/v1/chat/completions`, {
             method: 'POST',
             headers: {
               'Authorization': `Bearer ${this.poeApiKey}`,
               'Content-Type': 'application/json'
             },
             body: JSON.stringify({
               model: currentModelObj.id,
               messages: [
                 {
                   role: 'user',
                   content: [
                     {
                       type: 'text',
                       text: prompt
                     },
                     {
                       type: 'image_url',
                       image_url: {
                         url: base64Image
                       }
                     }
                   ]
                 }
               ],
               temperature: 0.1
             })
           });

           if (!response.ok) {
              const errorText = await response.text();
              let errorData;
              try { errorData = JSON.parse(errorText); } catch(e) { errorData = { error: { message: errorText } }; }
              console.error(`❌ Poe API 錯誤 (${response.status}):`, errorData);
              throw new Error(`Poe API 請求失敗 (${response.status}): ${errorData.error?.message || response.statusText}`);
           }

           const data = await response.json();
           content = data.choices?.[0]?.message?.content;

        } else {
           // 調用 OpenRouter API
           const keyPrefix = this.openRouterApiKey ? this.openRouterApiKey.substring(0, 15) + '...' : 'none';
           console.log(`🔑 Using OpenRouter API Key starting with: ${keyPrefix}`);
           
           response = await fetch(`${this.openRouterBaseUrl}/chat/completions`, {
             method: 'POST',
             headers: {
               'Authorization': `Bearer ${this.openRouterApiKey}`,
               'Content-Type': 'application/json',
               'HTTP-Referer': window.location.origin,
               'X-Title': 'Calorie AI - Food Recognition'
             },
             body: JSON.stringify({
               model: currentModelObj.id,
               messages: [
                 {
                   role: 'user',
                   content: [
                     {
                       type: 'text',
                       text: prompt
                     },
                     {
                       type: 'image_url',
                       image_url: {
                         url: base64Image
                       }
                     }
                   ]
                 }
               ],
               max_tokens: 1500,
               temperature: 0.1,
               response_format: { type: 'json_object' }
             })
           });

           if (!response.ok) {
              const errorText = await response.text();
              let errorData;
              try { errorData = JSON.parse(errorText); } catch (e) { errorData = { error: { message: errorText } }; }
              console.error(`❌ OpenRouter API 錯誤 (${response.status}):`, errorData);
              throw new Error(`OpenRouter API 請求失敗 (${response.status}): ${errorData.error?.message || response.statusText}`);
           }

           const data = await response.json();
           content = data.choices?.[0]?.message?.content;
        }
        
        // 詳細日誌輸出
        console.log(`=== AI 回應詳細日誌 (${currentModelObj.id}) ===`);
        console.log('提取的內容:', content);
        console.log('內容長度:', content?.length);
        console.log('========================');
        
        if (!content) {
          throw new Error('AI 回應格式錯誤：內容為空');
        }

        // 解析 AI 回應
        const result = this.parseAIResponse(content);
        
        // 成功識別，更新當前模型索引
        if (modelIndex !== this.currentModelIndex) {
          console.log(`✅ 模型 ${currentModelObj.id} 識別成功，更新為主要模型`);
          this.currentModelIndex = modelIndex;
        }
        
        return result;

      } catch (error) {
        console.error(`❌ 模型 ${currentModelObj.id} 識別失敗:`, error.message);
        lastError = error;
        // 繼續嘗試下一個模型
        continue;
      }
    }

    // 如果所有模型都失敗了
    throw new Error(`所有 AI 模型均識別失敗。最後錯誤: ${lastError?.message}`);
  }

  /**
   * 根據食物名稱獲取預設的升糖指數
   */
  getDefaultGlycemicIndex(foodName) {
    const lowGIFoods = ['蔬菜', '綠葉菜', '花椰菜', '番茄', '小黃瓜', '蛋白質', '雞肉', '魚肉', '豆腐', '蛋'];
    const mediumGIFoods = ['糙米', '燕麥', '全麥', '地瓜', '南瓜', '蘋果', '橘子'];
    const highGIFoods = ['白米', '白麵包', '馬鈴薯', '西瓜', '鳳梨', '糖果', '蛋糕'];
    
    const name = foodName.toLowerCase();
    
    if (lowGIFoods.some(food => name.includes(food))) {
      return 35; // 低GI
    } else if (highGIFoods.some(food => name.includes(food))) {
      return 75; // 高GI
    } else if (mediumGIFoods.some(food => name.includes(food))) {
      return 60; // 中GI
    }
    
    return 55; // 預設中等GI
  }

  /**
   * 構建食物識別提示詞
   */
  buildFoodIdentificationPrompt(additionalInfo) {
    return `你是一個專業的營養師和食物識別專家。請仔細分析這張圖片，識別其中的所有食物。

**極度重要指示（違反將導致程式錯誤）：**
1. 你的回應必須是合法的 JSON 格式，並且「只」包含 JSON，不能有任何額外文字或 Markdown 標籤。
2. 如果圖片中有多種食物，請**分別列出每一項**（例如「烤雞腿肉」、「烤馬鈴薯塊」、「青豆」應該是 foods 陣列中的獨立物件），**絕對不要**把它們合併成一個項目。
3. "name" 欄位只能包含單一食物的「簡短名稱」（例如："烤雞腿肉"），**絕對不要**在 name 欄位放入任何說明、評價或句子。
4. "confidence" 欄位必須是 0-1 之間的數字。只有當你對食物識別有足夠信心（≥0.6）時才包含在結果中。
5. "food_analysis" 欄位才用來放你對該單一食物的營養特點簡短分析。

請嚴格遵守以下 JSON 格式結構：

{
  "foods": [
    {
      "name": "具體的單一食物名稱（繁體中文，不可包含描述句）",
      "name_en": "Food name in English",
      "quantity": 1.0,
      "unit": "份",
      "calories": 250,
      "protein": 15.0,
      "carbs": 30.0,
      "fat": 8.0,
      "fiber": 3.0,
      "confidence": 0.85,
      "healthyIndex": 4,
      "gi_level": "低/中/高",
      "healthier_alternative": "更好的健康替代方案（如無則留空）",
      "food_analysis": "對這項食物的營養特點簡短分析（例如：富含優質蛋白質和 Omega-3...）"
    }
  ],
  "notes": "整體識別說明",
  "health_tips": "具體的生活化健康建議（例如：如何搭配更均衡、飯後建議等）",
  "overall_evaluation": "對整體食物組合的綜合評價與建議",
  "eating_order_suggestions": [
    {
      "food_name": "食物名稱（需與上方 foods 陣列中的 name 一致）",
      "reason": "為什麼建議在這個順序吃（基於營養學，如：先吃蔬菜增加飽足感減緩血糖上升、中段吃蛋白質...）"
    }
  ]
}

**欄位說明：**
- name: 單一食物的具體名稱（純名詞，如「烤雞腿肉」，不可有逗號或句號）
- name_en: 英文名稱
- quantity: 食物數量
- unit: 單位（份、個、碗、片等）
- calories: 該份量的卡路里
- protein/carbs/fat/fiber: 營養成分（克）
- confidence: 識別信心度數字（0.0 到 1.0 之間，必須≥0.6才包含）
- healthyIndex: 健康指數數字（1到5，5最健康）
- gi_level: 升糖指數預估（填寫：低、中或高）
- healthier_alternative: 更好的替代方案建議
- food_analysis: 針對單一食物的營養特點進行一句話的簡短評價
- notes: 整體識別說明
- health_tips: 更具體的健康建議與生活化提示
- overall_evaluation: 針對這餐/這些食物整體的營養價值給予1-2句話的綜合評價
- eating_order_suggestions: (可選) 當有2種以上食物時，基於營養學原理給出最佳的進食順序陣列（例如：蔬菜 -> 蛋白質 -> 碳水化合物）。陣列的順序即為建議的進食順序。

**特別注意：**
- 如果圖片中沒有食物或無法識別，請直接回應：{"foods": [], "notes": "未能識別到食物", "health_tips": "", "overall_evaluation": ""}
- 不要包含信心度低於0.6的食物
- 對於複雜的混合菜餚，請盡量分解為主要成分

${additionalInfo ? `\n額外資訊：${additionalInfo}` : ''}`;
  }

  /**
   * 解析 AI 回應
   */
  parseAIResponse(content) {
    console.log('=== 開始解析 AI 回應 ===');
    console.log('原始內容:', content);
    
    try {
      let jsonStr = '';
      
      // 首先嘗試提取 markdown 代碼塊中的 JSON
      const markdownMatch = content.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (markdownMatch) {
        console.log('發現 markdown 代碼塊格式');
        jsonStr = markdownMatch[1].trim();
      } else {
        // 嘗試提取普通 JSON 內容
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          console.log('發現普通 JSON 格式');
          jsonStr = jsonMatch[0];
        } else {
          console.error('無法找到任何 JSON 格式');
          console.error('內容預覽:', content.substring(0, 200) + '...');
          throw new Error(`無法找到有效的 JSON 回應。內容預覽: ${content.substring(0, 100)}...`);
        }
      }
      
      console.log('提取的 JSON 字串長度:', jsonStr.length);
      const result = JSON.parse(jsonStr);

      // 驗證回應格式
      if (!result.foods || !Array.isArray(result.foods)) {
        throw new Error('回應格式錯誤：缺少 foods 陣列');
      }

      // 設定最低信心度閾值
      const MIN_CONFIDENCE = 0.6;
      const originalFoodCount = result.foods.length;

      // 過濾低信心度和未知食物
      result.foods = result.foods.filter((food, index) => {
        const isValidFood = food.name && 
                           food.name.trim() !== '' && 
                           !food.name.includes('未知') && 
                           food.name !== '未知食物' &&
                           food.name.toLowerCase() !== 'unknown food';
        
        const hasGoodConfidence = (food.confidence || 0) >= MIN_CONFIDENCE;
        
        if (!isValidFood) {
          console.warn(`過濾無效食物 ${index + 1}: ${food.name}`);
        }
        if (!hasGoodConfidence) {
          console.warn(`過濾低信心度食物 ${index + 1}: ${food.name} (信心度: ${food.confidence})`);
        }
        
        return isValidFood && hasGoodConfidence;
      });

      // 檢查是否還有有效的食物
      if (result.foods.length === 0) {
        if (originalFoodCount > 0) {
          throw new Error(`識別信心度過低或無法識別圖片中的食物。原始識別數量: ${originalFoodCount}，請嘗試重新拍攝更清晰的照片`);
        } else {
          throw new Error('未能識別到任何食物，請確認圖片中包含食物並重新拍攝');
        }
      }

      console.log(`✅ 有效食物識別: ${result.foods.length}/${originalFoodCount}`);

      // 驗證每個食物項目
      result.foods.forEach((food, index) => {
        const requiredFields = ['name', 'quantity', 'unit', 'calories'];
        requiredFields.forEach(field => {
          if (food[field] === undefined || food[field] === null) {
            throw new Error(`食物項目 ${index + 1} 缺少必要欄位: ${field}`);
          }
        });

        // 確保數值類型正確
        food.quantity = parseFloat(food.quantity) || 1;
        food.calories = parseFloat(food.calories) || 0;
        food.protein = parseFloat(food.protein) || 0;
        food.carbs = parseFloat(food.carbs) || 0;
        food.fat = parseFloat(food.fat) || 0;
        food.fiber = parseFloat(food.fiber) || 0;
        food.confidence = parseFloat(food.confidence) || 0.5;
        food.healthyIndex = parseFloat(food.healthyIndex) || 3;

        // 處理血糖影響分析（提供預設值，因為簡化版不包含此功能）
        if (!food.glycemicAnalysis) {
          // 基於食物類型提供預設血糖分析
          const defaultGI = this.getDefaultGlycemicIndex(food.name);
          food.glycemicAnalysis = {
            glycemicIndex: defaultGI,
            glycemicLoad: Math.round(defaultGI * (food.carbs || 20) / 100),
            bloodSugarImpact: defaultGI < 55 ? '低' : defaultGI < 70 ? '中等' : '高',
            impactDescription: `基於食物類型的預估血糖影響`
          };
        }

        // 處理消化時間分析（提供預設值）
        if (!food.digestionAnalysis) {
          food.digestionAnalysis = {
            digestionTime: '2-3小時',
            satietyDuration: '3-4小時',
            digestionDescription: '消化速度適中'
          };
        }

        // 處理個人化建議（提供預設值）
        if (!food.personalizedAdvice) {
          food.personalizedAdvice = {
            weightLossMatch: food.healthyIndex || 3,
            muscleBuildingMatch: food.protein > 10 ? 4 : 3,
            maintenanceMatch: 3,
            calorieImpact: food.calories > 300 ? '高' : food.calories > 150 ? '適中' : '低',
            recommendation: '適量食用，搭配均衡飲食'
          };
        }
      });

      // 處理進食順序建議
      // 完全依賴 AI 提供的 eating_order_suggestions，確保它是一個陣列或可以被 UI 處理的格式
      if (!result.eating_order_suggestions) {
        if (result.eatingOrderSuggestion) {
           // 如果 AI 錯誤地使用了舊的駝峰式命名，將它修正過來
           result.eating_order_suggestions = result.eatingOrderSuggestion;
        } else {
           result.eating_order_suggestions = [];
        }
      } else if (!Array.isArray(result.eating_order_suggestions) && result.eating_order_suggestions.order) {
        // 如果 AI 返回了帶有 order 屬性的物件，則保留它
      } else if (!Array.isArray(result.eating_order_suggestions) && typeof result.eating_order_suggestions !== 'object') {
        // 如果它既不是陣列也不是物件（例如單純的字串），將它包裝成陣列
        result.eating_order_suggestions = [result.eating_order_suggestions];
      }
      
      // 同步舊版屬性以防 UI 還有其他地方用到
      result.eatingOrderSuggestion = result.eating_order_suggestions;

      if (!result.overall_analysis) {
        const avgHealthScore = result.foods.reduce((sum, food) => sum + food.healthyIndex, 0) / result.foods.length;
        result.overall_analysis = {
          nutritional_balance: avgHealthScore >= 4 ? '營養均衡度優秀' : avgHealthScore >= 3 ? '營養均衡度良好' : '建議增加蔬果攝取',
          recommendation: avgHealthScore >= 4 ? '繼續保持這樣的飲食習慣' : avgHealthScore >= 3 ? '可適當增加蔬果比例' : '建議選擇更多天然未加工食物'
        };
      }

      // 過濾掉信心度過低的食物（作為雙重保險）
      if (result.foods) {
        result.foods = result.foods.filter(food => food.confidence === undefined || food.confidence >= 0.6);
      }

      return result;
    } catch (error) {
      console.error('=== JSON 解析失敗 ===');
      console.error('錯誤詳情:', error);
      console.error('原始回應:', content);
      
      // 嘗試備用解析邏輯
      console.log('嘗試備用解析邏輯...');
      try {
        return this.fallbackParseResponse(content);
      } catch (fallbackError) {
        console.error('備用解析也失敗:', fallbackError);
        throw new Error(`解析 AI 回應失敗: ${error.message}。備用解析錯誤: ${fallbackError.message}`);
      }
    }
  }

  /**
   * 備用解析邏輯
   */
  fallbackParseResponse(content) {
    console.log('執行備用解析邏輯');
    
    // 嘗試從文本中提取關鍵信息
    const lines = content.split('\n').map(line => line.trim()).filter(line => line);
    
    // 查找可能的食物名稱
    const foodNamePatterns = [
      /食物[：:](.*)/,
      /名稱[：:](.*)/,
      /識別到[：:]?(.*)/,
      /這是(.*)/
    ];
    
    let foodName = '未知食物';
    for (const line of lines) {
      for (const pattern of foodNamePatterns) {
        const match = line.match(pattern);
        if (match) {
          foodName = match[1].trim();
          break;
        }
      }
      if (foodName !== '未知食物') break;
    }
    
    // 查找可能的卡路里信息
    const caloriePattern = /(\d+(?:\.\d+)?)\s*(?:卡路里|卡|kcal|cal)/i;
    let calories = 0;
    for (const line of lines) {
      const match = line.match(caloriePattern);
      if (match) {
        calories = parseFloat(match[1]);
        break;
      }
    }
    
    console.log(`備用解析結果: 食物=${foodName}, 卡路里=${calories}`);
    
    // 構建基本的回應格式
    return {
      foods: [{
        name: foodName,
        name_en: foodName,
        quantity: 1,
        unit: '份',
        calories: calories || 100, // 預設值
        protein: 0,
        carbs: 0,
        fat: 0,
        confidence: 0.3, // 低信心度
        healthyIndex: 3 // 預設健康指數
      }],
      total_calories: calories || 100,
      meal_suggestion: 'snack',
      notes: '此結果由備用解析邏輯生成，準確性較低，建議重新拍攝或手動輸入'
    };
  }

  /**
   * 根據食物名稱獲取營養建議
   */
  async getNutritionAdvice(foodName, calories, userGoals = {}) {
    try {
      const prompt = `請為以下食物提供營養建議：

食物名稱: ${foodName}
卡路里: ${calories}
用戶目標: ${JSON.stringify(userGoals)}

請以繁體中文回應，包含:
1. 這個食物的營養特點
2. 適合的食用時機
3. 搭配建議
4. 注意事項

請保持回應簡潔實用，約100-200字。`;

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Calorie AI - Nutrition Advice'
        },
        body: JSON.stringify({
          model: 'anthropic/claude-3-haiku',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 500,
          temperature: 0.7
        })
      });

      if (!response.ok) {
        throw new Error(`API 請求失敗: ${response.statusText}`);
      }

      const data = await response.json();
      return data.choices?.[0]?.message?.content || '暫無營養建議';
    } catch (error) {
      console.error('獲取營養建議失敗:', error);
      return '暫無營養建議';
    }
  }

  /**
   * 生成每日營養總結
   */
  async generateDailySummary(foodRecords, calorieGoal) {
    try {
      // 確保 foodRecords 是陣列
      if (!Array.isArray(foodRecords)) {
        console.warn('⚠️ generateDailySummary: foodRecords 不是陣列，使用空陣列');
        foodRecords = [];
      }
      
      const totalCalories = foodRecords.reduce((sum, record) => sum + (record.calories || 0), 0);
      const totalProtein = foodRecords.reduce((sum, record) => sum + (record.protein || 0), 0);
      const totalCarbs = foodRecords.reduce((sum, record) => sum + (record.carbs || 0), 0);
      const totalFat = foodRecords.reduce((sum, record) => sum + (record.fat || 0), 0);

      const foodList = foodRecords.map(record => `${record.food_name} (${record.calories}卡)`).join(', ');

      const prompt = `請分析今日的飲食記錄並提供總結建議：

今日攝取:
- 總卡路里: ${totalCalories}卡 (目標: ${calorieGoal}卡)
- 蛋白質: ${totalProtein}g
- 碳水化合物: ${totalCarbs}g
- 脂肪: ${totalFat}g

食物清單: ${foodList}

請以繁體中文提供:
1. 今日飲食評估
2. 營養平衡分析
3. 明日改善建議

請保持回應簡潔實用，約150-250字。`;

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Calorie AI - Daily Summary'
        },
        body: JSON.stringify({
          model: 'anthropic/claude-3-haiku',
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 600,
          temperature: 0.7
        })
      });

      if (!response.ok) {
        throw new Error(`API 請求失敗: ${response.statusText}`);
      }

      const data = await response.json();
      return data.choices?.[0]?.message?.content || '暫無每日總結';
    } catch (error) {
      console.error('生成每日總結失敗:', error);
      return '暫無每日總結';
    }
  }
}

// 創建服務實例
export const aiService = new AIService();

// 導出服務類別
export { AIService };

// 預設導出
export default aiService;